import type { IconType } from "react-icons";
import { FaChartLine, FaShip, FaBolt } from "react-icons/fa6";
import {
  FaBuilding,
  FaDatabase,
  FaFolder,
  FaHdd,
  FaSearch,
  FaTable,
  FaUser,
} from "react-icons/fa";

import { AvailableFeatureFlags, AvailableRoles } from "@/apiClient";
import type { User } from "@/apiClient/models/User";
import { hasFeatureFlag, hasRole } from "@/utils";

type NavPath =
  | "/"
  | "/admin/folder-config"
  | "/admin/storage-ops"
  | "/data/assets/search"
  | "/data/browse"
  | "/data/sets"
  | "/data/volumes"
  | "/organizations"
  | "/realassets"
  | "/user";

export type NavItem = {
  key: string;
  title: string;
  to?: NavPath;
  icon?: IconType;
  group?: string;
  comingSoon?: boolean;
  featureFlag?: AvailableFeatureFlags;
  role?: AvailableRoles;
  superUser?: boolean;
};

export type NavSection = {
  key: string;
  title: string;
  icon: IconType;
  comingSoon?: boolean;
  superUser?: boolean;
  children: NavItem[];
};

export const NAV_SECTIONS: NavSection[] = [
  {
    key: "data",
    title: "Data",
    icon: FaChartLine,
    children: [
      // TODO: Restore this nav item when the Data Overview page is ready to ship.
      // {
      //   key: "data-overview",
      //   title: "Data Overview",
      //   comingSoon: true,
      // },
      {
        key: "asset-search",
        title: "Asset Search",
        to: "/data/assets/search",
        icon: FaSearch,
        group: "Find & Explore",
      },
      {
        key: "browse",
        title: "File Explorer",
        to: "/data/browse",
        icon: FaFolder,
        group: "Find & Explore",
      },
      {
        key: "sets",
        title: "Data Sets",
        to: "/data/sets",
        icon: FaTable,
        featureFlag: AvailableFeatureFlags.DATA_SET,
        group: "Catalog",
      },
      {
        key: "volumes",
        title: "Volumes",
        to: "/data/volumes",
        icon: FaHdd,
        group: "Catalog",
      },
    ],
  },
  {
    key: "expeditions",
    title: "Expeditions",
    icon: FaShip,
    children: [
      // TODO: Restore this nav item when Manifest Tracking is ready to ship.
      // {
      //   key: "manifest-tracking",
      //   title: "Manifest Tracking",
      //   comingSoon: true,
      // },
      // TODO: Restore this nav item when the Cruises page is ready to ship.
      // {
      //   key: "cruises",
      //   title: "Cruises",
      //   comingSoon: true,
      // },
      {
        key: "real-assets",
        title: "Real Assets",
        to: "/realassets",
        icon: FaShip,
        featureFlag: AvailableFeatureFlags.REAL_ASSET,
      },
      // TODO: Restore this nav item when Geospatial Views are ready to ship.
      // {
      //   key: "map",
      //   title: "Geospatial Views",
      //   comingSoon: true,
      // },
    ],
  },
  {
    key: "admin",
    title: "Admin",
    icon: FaBolt,
    superUser: true,
    children: [
      {
        key: "users",
        title: "Users",
        to: "/user",
        icon: FaUser,
        superUser: true,
      },
      {
        key: "organizations",
        title: "Organizations",
        to: "/organizations",
        icon: FaBuilding,
        superUser: true,
      },
      {
        key: "folder-config",
        title: "Folder Config",
        to: "/admin/folder-config",
        icon: FaFolder,
        role: AvailableRoles.REAL_ASSET_CREATOR,
      },
      {
        key: "storage-ops",
        title: "Storage Ops",
        to: "/admin/storage-ops",
        icon: FaDatabase,
        superUser: true,
      },
    ],
  },
  // TODO: Restore this section and its FaBook import when the Documentation Hub is ready to ship.
  // {
  //   key: "documentation",
  //   title: "Documentation Hub",
  //   icon: FaBook,
  //   comingSoon: true,
  //   children: [
  //     {
  //       key: "docs-overview",
  //       title: "Overview",
  //       comingSoon: true,
  //     },
  //     {
  //       key: "docs-plans-and-agreements",
  //       title: "Plans & Agreements",
  //       comingSoon: true,
  //       group: "Governance",
  //     },
  //     {
  //       key: "docs-templates-and-standards",
  //       title: "Templates & Standards",
  //       comingSoon: true,
  //       group: "Operational Resources",
  //     },
  //     {
  //       key: "docs-sensor-and-calibration",
  //       title: "Sensor & Calibration Docs",
  //       comingSoon: true,
  //       group: "Operational Resources",
  //     },
  //   ],
  // },
];

export const normalizeNavPath = (path = ""): string => {
  if (!path) {
    return "/";
  }

  const stripped = path.replace(/\/+$/, "");
  return stripped === "" ? "/" : stripped;
};

export const isNavPathActive = (
  pathname: string,
  candidatePath?: string
): boolean => {
  if (!candidatePath) {
    return false;
  }

  const activePath = normalizeNavPath(pathname);
  const normalizedCandidate = normalizeNavPath(candidatePath);

  if (normalizedCandidate === "/") {
    return activePath === "/";
  }

  return (
    activePath === normalizedCandidate ||
    activePath.startsWith(`${normalizedCandidate}/`)
  );
};

export const userHasRole = (
  user: User | null,
  role: AvailableRoles
): boolean => {
  const organizationRoles = (
    user?.organization as
      | { roles?: Array<{ name?: string | null }> }
      | undefined
  )?.roles;

  return (
    hasRole(user, role) ||
    organizationRoles?.some(({ name }) => name === role) === true
  );
};

const canViewNavItem = (user: User | null, item: NavItem): boolean => {
  if (item.featureFlag && !(user && hasFeatureFlag(user, item.featureFlag))) {
    return false;
  }

  if (item.superUser && user?.is_superuser !== true) {
    return false;
  }

  if (item.role && !(user && userHasRole(user, item.role))) {
    return false;
  }

  return true;
};

export const getVisibleNavSections = (user: User | null): NavSection[] =>
  NAV_SECTIONS.filter((section) =>
    section.children.some((item) => canViewNavItem(user, item))
  );

export const getVisibleNavItems = (
  section: NavSection,
  user: User | null
): NavItem[] => section.children.filter((item) => canViewNavItem(user, item));

export const getSectionDefaultPath = (
  section: NavSection,
  user: User | null
): NavPath | null => {
  const firstNavigableItem = getVisibleNavItems(section, user).find(
    (item) => item.to && !item.comingSoon
  );

  return firstNavigableItem?.to ?? null;
};

export const getActiveNavSection = (
  pathname: string,
  user: User | null
): NavSection | null => {
  const visibleSections = getVisibleNavSections(user);
  const activeSection = visibleSections.find((section) =>
    section.children.some(
      (item) => item.to && isNavPathActive(pathname, item.to)
    )
  );

  return activeSection ?? null;
};
